// EF3.1 baseline constants and toast-bread references used in the Reuse (human) methodology.
// All values follow the Food Donation/Redistribution specification (05.02.2025) and are kept in
// one place so that methodology updates only touch this module.

// Substitution mechanics
export const substitutionRate = 0.33; // SR – share of donations that replaces new production
export const donationEfficiency = 0.78; // FWDE – share of donations that is actually consumed

// Waste-treatment emission factors (EWT) per kg of material diverted from disposal
export const ewtCo2ePerKg = 0.0554;      // kg CO2 eq / kg
export const ewtWaterM3PerKg = 0.0370;   // m3 world eq / kg
export const ewtLandPerKg = 0.242;       // point / kg  // TODO: Verify point value with methodology source

// Transport intensities (ATE) per tonne-km for the redistribution leg (EF v3.1)
export const ateCo2ePerTkm = 1.58;       // kg CO2 eq / tkm (EF v3.1)
export const ateWaterPerTkm = 0.163;     // m3 world eq / tkm (EF v3.1)
export const ateLandPerTkm = 9.29;       // point / tkm (EF v3.1)

// Toast bread reference footprints (EFoP_toast) from Agribalyse ("Sandwich loaf")
export const toastBreadCo2ePerKg = 1.55;         // kg CO2 eq / kg toast bread (Agribalyse)
export const toastBreadWaterM3PerKg = 0.383;     // m3 world eq / kg toast bread (Agribalyse)
export const toastBreadLandPerKg = 93.8;         // point / kg toast bread (Agribalyse)

// Presentation helpers: labels for EF3.1 units (Agribalyse notation)
export const LAND_UNIT_LABEL_EF31 = 'point';
export const WATER_UNIT_LABEL_EF31 = 'm3 world eq';

// ============================================================================
// Agribalyse v3.1 Reference Values for Environmental Impact Comparisons
// ============================================================================
// These values are used to create tangible comparisons for users to understand
// the scale of environmental savings. All values are per 1 kg of product.
// Source: Agribalyse Database v3.1

export const AGRIBALYSE_REFERENCES = {
  // Potato (raw) - Low environmental impact reference
  potato: {
    name: 'Potato',
    emoji: '🥔',
    climateImpact: 0.29,      // kg CO₂eq per kg
    waterScarcity: 0.06,      // m³ world eq per kg
    landUse: 8.5,             // points per kg
  },

  // Chicken (raw) - Medium environmental impact reference
  chicken: {
    name: 'Chicken',
    emoji: '🍗',
    climateImpact: 6.9,       // kg CO₂eq per kg
    waterScarcity: 0.6,       // m³ world eq per kg
    landUse: 55,              // points per kg
  },

  // Beef (boneless, raw) - High environmental impact reference
  beef: {
    name: 'Beef',
    emoji: '🥩',
    climateImpact: 60.0,      // kg CO₂eq per kg
    waterScarcity: 1.2,       // m³ world eq per kg
    landUse: 220,             // points per kg
  },
};

/**
 * Find the most appropriate reference product for a given environmental impact value
 *
 * @param {number} value - The environmental impact value to compare
 * @param {string} indicator - The indicator type: 'climate', 'water', or 'land'
 * @returns {Object} - Reference product object with comparison data
 */
export function findBestReference(value, indicator) {
  const absValue = Math.abs(value);

  // Map indicator to property names
  const indicatorMap = {
    climate: 'climateImpact',
    water: 'waterScarcity',
    land: 'landUse',
  };

  const property = indicatorMap[indicator];
  if (!property) return null;

  const references = Object.values(AGRIBALYSE_REFERENCES);

  // Find the reference with the closest impact value
  let bestMatch = references[0];
  let smallestDifference = Math.abs(absValue / bestMatch[property] - 1);

  for (const ref of references) {
    const refValue = ref[property];
    const difference = Math.abs(absValue / refValue - 1);

    // Prefer references that give results between 0.5 and 50 kg
    const equivalentKg = absValue / refValue;
    const isInGoodRange = equivalentKg >= 0.5 && equivalentKg <= 50;

    if (isInGoodRange && (difference < smallestDifference || !bestMatch)) {
      bestMatch = ref;
      smallestDifference = difference;
    }
  }

  return {
    product: bestMatch,
    equivalentKg: absValue / bestMatch[property],
  };
}

/**
 * Descriptions of what each environmental indicator measures
 */
export const INDICATOR_DESCRIPTIONS = {
  climate: {
    title: '☁️ Climate Impact (kg CO₂eq)',
    shortDescription: 'The "climate backpack"',
    fullDescription: 'This is the "climate backpack." It captures all greenhouse gases and converts their total impact into an equivalent amount of CO₂.',
  },
  water: {
    title: '💧 Water Scarcity (m³ world eq)',
    shortDescription: 'Scarcity-weighted water use',
    fullDescription: 'This value measures water consumption weighted by regional water scarcity. It answers the question: How much does the water usage contribute to global water scarcity?',
  },
  land: {
    title: '🌲 Land Use (point)',
    shortDescription: 'Soil quality score',
    fullDescription: 'This is a "soil quality score." The value measures how much an ecosystem loses in quality and biodiversity due to agriculture compared to its natural state.',
  },
};

